import React, { useRef, useState } from "react";
import { Dialog, Portal, CloseButton, Button, Table, For, VStack, HStack, Box, Text } from "@chakra-ui/react";
import { LuFocus, LuRotateCcw, LuTrash2, LuTvMinimalPlay } from "react-icons/lu";
import {
  JulianDate,
  Math as CesiumMath,
  CzmlDataSource,
  ClockRange,
  HeadingPitchRange,
  CallbackProperty,
  Color,
  ColorBlendMode,
  Cartesian2,
  Cartesian3,
  ShadowMode,
  CustomShader,
  LightingModel,
  DirectionalLight,
} from "cesium";
import { showToast } from "../../../utilies/Utility";
import { showBBoxOnMap } from "../../../utilies/ProjectUtility";
import Type from "../../../utilies/Types";
import { formatReadableDateTime, quoteNaNTokens } from "../../../utilies/QueryUtility";
import { useAppStore } from "../../../context/store";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine, ReferenceDot } from "recharts";

const MPH_PER_MS = 2.2369362920544;
const MIN_DT_MS = 200;

function safeNum(v, maxAbs = 1e9) {
  const n = Number(v);
  return Number.isFinite(n) && Math.abs(n) <= maxAbs ? n : null;
}

function fmtNum(v, digits = 2) {
  return v === null ? "—" : Number(v).toFixed(digits);
}

function fmtCoord(v) {
  return v === null ? "—" : Number(v).toFixed(6);
}

function upperBoundTs(arr, key) {
  let lo = 0,
    hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t <= key) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

function haversineMeters(lat1, lon1, lat2, lon2) {
  if ([lat1, lon1, lat2, lon2].some((v) => v == null)) return null;
  const R = 6371000; // Earth radius (m)
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1),
    φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function neutralizeModelLighting(viewer, entity) {
  const scene = viewer.scene;

  // 1) Stop HDR exposure shifts which can wash colors
  scene.highDynamicRange = false; // keep exposure fixed

  // 2) Turn off environment image-based lighting on the model
  if (entity.model?.imageBasedLightingFactor) {
    // [diffuse, specular] scales — 0 disables that channel
    entity.model.imageBasedLightingFactor = new Cartesian2(0.0, 0.0);
  }

  // 3) Fix a neutral directional light so colors don't drift with time-of-day
  if (DirectionalLight) {
    scene.light = new DirectionalLight({
      // top-down-ish light; keep it stable
      direction: Cartesian3.normalize(new Cartesian3(0.5, 0.5, -1.0), new Cartesian3()),
      intensity: 1.0,
    });
  }

  // 4) Ensure no accidental color tinting/blending on the model
  if (entity.model?.color) entity.model.color = Color.WHITE; // no tint
  if (entity.model?.colorBlendMode) entity.model.colorBlendMode = ColorBlendMode.REPLACE;

  // 5) Optional: disable shadows to avoid extra darkening
  if (entity.model?.shadows !== undefined) entity.model.shadows = ShadowMode.DISABLED;

  // 6) (Best accuracy) Force UNLIT to match source albedo exactly
  if (CustomShader && LightingModel) {
    entity.model.customShader = new CustomShader({
      lightingModel: LightingModel.UNLIT, // ignore all lighting; show true baseColor
    });
  }
}

const QueryTable = () => {
  // state
  const showQeuryMenuTable = useAppStore((s) => s.showQeuryMenuTable);
  const qeuryMenuTable = useAppStore((s) => s.qeuryMenuTable);

  // setters / refs
  const setShowQeuryMenuTable = useAppStore.getState().setShowQeuryMenuTable;
  const setQeuryMenuTable = useAppStore.getState().setQeuryMenuTable;
  const viewerRef = useAppStore.getState().viewerRef;

  const [loadingPercents, setLoadingPercents] = useState({});
  const [telemetry, setTelemetry] = useState(null); // latest point (roll, pitch, speed, ...)
  const [heightWindow, setHeightWindow] = useState([]); // now holds the FULL height series [{t,h}]
  const [chartCursor, setChartCursor] = useState(null); // {t,h} — current time & height on the chart

  const lastLoggedTsRef = useRef(null);
  const lastChartEndTsRef = useRef(null);

  // Full, normalized, sorted series: [{t,h,lat,lon,und,nv,ev,uv,roll,pitch,az}]
  const geoSeriesRef = useRef([]);

  /** Fetch with progress; returns text. */
  async function fetchWithProgress(url, onProgress) {
    const res = await fetch(url, {
      headers: { Accept: "application/geo+json, application/json;q=0.9, text/plain;q=0.8, */*;q=0.1" },
    });
    if (!res.ok) throw new Error(`Failed to fetch. HTTP ${res.status}`);

    const contentLength = res.headers.get("Content-Length");
    if (!contentLength) {
      console.warn("No Content-Length header. Cannot compute progress.");
      return await res.text();
    }
    const total = parseInt(contentLength, 10);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let loaded = 0,
      result = "";
    // Stream read with progress
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      loaded += value.length;
      result += decoder.decode(value, { stream: true });
      onProgress?.(Math.round((loaded / total) * 100));
    }
    result += decoder.decode();
    return result;
  }

  /** Build CZML + fill geoSeriesRef with full telemetry fields. */
  function geojsonToTruckCzml(geojson, options = {}) {
    const modelUrl = options.modelUrl ?? "https://matrix.gatik.ai/static/gatiktruck.glb";
    const modelScale = options.modelScale ?? 1.0;
    const trailSeconds = options.trailSeconds ?? 60;
    const multiplier = options.multiplier ?? 1;
    const minDtMs = options.minDtMs ?? MIN_DT_MS;

    const raw = [];
    const routePositions = [];
    const getH = (c) => (Array.isArray(c) && c.length >= 3 ? Number(c[2]) : 0);

    // Collect raw points from GeoJSON
    for (const f of geojson.features || []) {
      if (!f?.geometry) continue;
      if (f.geometry.type === "Point") {
        const ts = Number(f.properties?.ts);
        if (!Number.isFinite(ts)) continue;
        const [lon, lat] = f.geometry.coordinates;
        const h = getH(f.geometry.coordinates);
        raw.push({
          t: ts,
          lon: +lon,
          lat: +lat,
          h,
          und: f.properties?.und,
          nv: f.properties?.nv,
          ev: f.properties?.ev,
          uv: f.properties?.uv,
          roll: f.properties?.roll,
          pitch: f.properties?.pitch,
          az: f.properties?.az,
        });
        const L = routePositions.length;
        if (L < 3 || routePositions[L - 3] !== +lon || routePositions[L - 2] !== +lat || routePositions[L - 1] !== h) {
          routePositions.push(+lon, +lat, h + (f.properties?.und || 0));
        }
      } else if (f.geometry.type === "LineString") {
        const coords = f.geometry.coordinates || [];
        const startTs = Number(f.properties?.ts ?? Date.now());
        const dt = Number(f.properties?.dt ?? 1000);
        coords.forEach((c, i) => {
          raw.push({
            t: startTs + i * dt,
            lon: +c[0],
            lat: +c[1],
            h: getH(c),
            und: f.properties?.und,
            nv: f.properties?.nv,
            ev: f.properties?.ev,
            uv: f.properties?.uv,
            roll: f.properties?.roll,
            pitch: f.properties?.pitch,
            az: f.properties?.az,
          });
          routePositions.push(+c[0], +c[1], getH(c) + (f.properties?.und || 0));
        });
      }
    }
    if (!raw.length) throw new Error("No usable samples.");

    // Sort & decimate
    raw.sort((a, b) => a.t - b.t);
    const samples = [];
    let lastT = -Infinity;
    for (const s of raw) {
      if (s.t - lastT < minDtMs) continue;
      samples.push(s);
      lastT = s.t;
    }
    if (samples.length < 2) throw new Error("Not enough samples after normalization.");

    // Normalize to safe series (clamp absurd numbers)
    geoSeriesRef.current = samples.map((s) => ({
      t: s.t,
      h: safeNum(s.h, 1e7),
      lat: safeNum(s.lat, 1e3),
      lon: safeNum(s.lon, 1e3),
      und: safeNum(s.und, 1e5),
      nv: safeNum(s.nv, 1e4), // velocities clamped
      ev: safeNum(s.ev, 1e4),
      uv: safeNum(s.uv, 1e4),
      roll: safeNum(s.roll, 1e4),
      pitch: safeNum(s.pitch, 1e4),
      az: safeNum(s.az, 1e6),
    }));

    // Build CZML time bounds
    const minT = samples[0].t;
    const maxT = samples[samples.length - 1].t;
    const epochISO = new Date(minT).toISOString();
    const startISO = epochISO;
    const endISO = new Date(maxT).toISOString();

    const cartoDegrees = [];
    for (const s of samples) {
      cartoDegrees.push((s.t - minT) / 1000, s.lon, s.lat, s.h + (s.und || 0));
    }

    const czml = [
      {
        id: "document",
        version: "1.0",
        clock: { interval: `${startISO}/${endISO}`, currentTime: startISO, multiplier, range: "LOOP_STOP" },
      },
    ];

    if (routePositions.length >= 6) {
      czml.push({
        id: "route_polyline",
        polyline: {
          positions: { cartographicDegrees: routePositions },
          width: 10,
          material: { solidColor: { color: { rgba: [0, 153, 255, 160] } } },
        },
      });
    }

    czml.push({
      id: "truck",
      availability: `${startISO}/${endISO}`,
      position: {
        epoch: epochISO,
        cartographicDegrees: cartoDegrees,
        interpolationAlgorithm: "LINEAR",
        interpolationDegree: 1,
      },
      orientation: { velocityReference: "#position" },
      model: { gltf: modelUrl, scale: modelScale, minimumPixelSize: 64 },
      path: {
        leadTime: 0,
        trailTime: trailSeconds,
        width: 10,
        material: { solidColor: { color: { rgba: [255, 255, 0, 255] } } },
      },
    });

    // ⬇️ NEW: Fill the chart with the FULL height series (no 60s window)
    setHeightWindow(geoSeriesRef.current.map(({ t, h }) => ({ t, h })));
    setChartCursor(null); // reset cursor until logger runs
    lastChartEndTsRef.current = null;

    return czml;
  }

  function dynamicWidth(viewer, baseWidth = 10) {
    return new CallbackProperty(() => {
      const height = viewer.camera.positionCartographic.height;

      if (height < 150) return baseWidth + 20;
      if (height < 2000) return baseWidth + 10;
      if (height < 10000) return baseWidth + 5;
      return baseWidth;
    }, false);
  }
  /** Compute speed (mph) using nv/ev if valid; otherwise fallback via neighbor spatial delta. */
  function computeSpeedMphFromPoint(series, idx) {
    const p = series[idx];
    if (!p) return null;

    // Primary: ground speed from nv & ev (m/s)
    if (p.nv != null && p.ev != null) {
      const ms = Math.sqrt(p.nv * p.nv + p.ev * p.ev);
      if (Number.isFinite(ms)) return ms * MPH_PER_MS;
    }

    // Fallback: from neighbor spatial diff
    const prev = series[idx - 1];
    const next = series[idx + 1];

    // pick the better neighbor by closeness in time
    let neighbor = null;
    if (prev && next) {
      neighbor = Math.abs(p.t - prev.t) <= Math.abs(next.t - p.t) ? prev : next;
    } else {
      neighbor = prev || next || null;
    }

    if (!neighbor) return null;

    const dt = Math.abs(p.t - neighbor.t) / 1000; // seconds
    if (!dt) return null;

    const dMeters = haversineMeters(p.lat, p.lon, neighbor.lat, neighbor.lon);
    if (dMeters == null) return null;

    const ms = dMeters / dt; // m/s
    if (!Number.isFinite(ms)) return null;
    return ms * MPH_PER_MS;
  }

  // ---------- load & attach logger ----------
  async function loadGeoJsonAsTruckCzml(viewer, geojsonUrl, options = {}, index) {
    try {
      const raw = await fetchWithProgress(geojsonUrl, (percent) => {
        setLoadingPercents((prev) => ({ ...prev, [index]: percent }));
        if (percent === 100) {
          setLoadingPercents((prev) => ({ ...prev, [index]: null }));
          setShowQeuryMenuTable(false);
        }
      });
      const sanitized = quoteNaNTokens(raw);

      let geojson;
      try {
        geojson = JSON.parse(sanitized);
      } catch (e) {
        throw new Error(`Invalid JSON even after sanitizing NaN: ${e.message}`);
      }

      const czml = geojsonToTruckCzml(geojson, options);

      const czmlDs = new CzmlDataSource();
      await viewer.dataSources.add(czmlDs);
      await czmlDs.load(czml);

      const route = czmlDs.entities.getById("route_polyline");
      if (route) {
        route.polyline.width = dynamicWidth(viewer, 5);
      }

      const truck = czmlDs.entities.getById("truck");
      if (truck) {
        viewer.trackedEntity = truck;
        truck.path.width = dynamicWidth(viewer, 5);
        if (viewer._geojsonLogger) {
          viewer.scene.preUpdate.removeEventListener(viewer._geojsonLogger);
          viewer._geojsonLogger = null;
        }

        const logger = () => {
          const nowMs = JulianDate.toDate(viewer.clock.currentTime).getTime();
          const src = geoSeriesRef.current;
          if (!src.length) return;

          // Find nearest index to current time
          const idxLE = upperBoundTs(src, nowMs);
          let idxNearest;
          if (idxLE < 0) idxNearest = 0;
          else if (idxLE >= src.length - 1) idxNearest = src.length - 1;
          else {
            const a = src[idxLE],
              b = src[idxLE + 1];
            idxNearest = Math.abs(a.t - nowMs) <= Math.abs(b.t - nowMs) ? idxLE : idxLE + 1;
          }

          const pt = src[idxNearest];
          if (!pt) return;

          // Update telemetry only when timestamp changes
          if (pt.t !== lastLoggedTsRef.current) {
            lastLoggedTsRef.current = pt.t;

            const speedMph = computeSpeedMphFromPoint(src, idxNearest);

            setTelemetry({
              speedMph: Number.isFinite(speedMph) ? speedMph : null,
              lat: pt.lat,
              lon: pt.lon,
              h: pt.h,
              und: pt.und,
              nv: pt.nv,
              uv: pt.uv,
              roll: pt.roll,
              pitch: pt.pitch,
              az: pt.az,
            });

            // ⬇️ NEW: drive the chart cursor (current time/height marker)
            setChartCursor({ t: pt.t, h: pt.h });
          }

          // ⛔️ Removed sliding 60s window logic — we keep the FULL series in the chart
        };

        viewer._geojsonLogger = logger;
        viewer.scene.preUpdate.addEventListener(logger);

        setTimeout(() => {
          viewer.zoomTo(truck, new HeadingPitchRange(0.0, CesiumMath.toRadians(-30), 150));
        }, 200);
        neutralizeModelLighting(viewer, truck);
      } else {
        await viewer.zoomTo(czmlDs, new HeadingPitchRange(0.0, CesiumMath.toRadians(-30), 150));
      }

      // Keep animation off; scrubbing timeline still drives currentTime & logger
      viewer.clock.shouldAnimate = false;
      if (viewer.timeline?.container) viewer.timeline.container.style.display = "";
      if (viewer.animation?.container) viewer.animation.container.style.display = "";

      return czmlDs;
    } catch (err) {
      console.error("Error loading GeoJSON as Truck CZML:", err);
      const msg = err?.message?.includes("HTTP")
        ? `Server error: ${err.message}`
        : err?.name === "TypeError" && err?.message?.includes("fetch")
        ? "Network error: could not reach the server."
        : err?.message || "Unexpected error while loading data.";
      showToast(msg, Type.NOTIFICATION.ERROR);
      return null;
    }
  }

  /** Reset viewer + clear listeners and UI state. */
  function resetViewer(viewer, opts = {}) {
    try {
      if (!viewer || viewer.isDestroyed?.()) return;

      if (viewer._geojsonLogger) {
        viewer.scene.preUpdate.removeEventListener(viewer._geojsonLogger);
        viewer._geojsonLogger = null;
      }

      viewer.clock.shouldAnimate = false;
      viewer.trackedEntity = undefined;
      viewer.selectedEntity = undefined;

      const truck = viewer.entities.getById("truck");
      if (truck) viewer.entities.remove(truck);
      const route = viewer.entities.getById("route_polyline");
      if (route) viewer.entities.remove(route);

      viewer.dataSources._dataSources.filter((ds) => ds instanceof CzmlDataSource).forEach((ds) => viewer.dataSources.remove(ds, true));

      const start = JulianDate.now();
      const stop = JulianDate.addSeconds(start, opts.windowSeconds ?? 60, new JulianDate());

      const clock = viewer.clock;
      clock.startTime = start.clone();
      clock.stopTime = stop;
      clock.currentTime = start.clone();
      clock.clockRange = ClockRange.UNBOUNDED;
      clock.multiplier = opts.multiplier ?? 1;
      clock.shouldAnimate = false;

      // Clear series & UI
      geoSeriesRef.current = [];
      setHeightWindow([]);
      setTelemetry(null);
      setChartCursor(null); // ⬅️ NEW: clear cursor
      lastLoggedTsRef.current = null;
      lastChartEndTsRef.current = null;

      if (viewer.timeline && viewer.timeline.zoomTo) {
        viewer.timeline.zoomTo(clock.startTime, clock.stopTime);
      }
    } catch (err) {
      console.error("Error resetting viewer:", err);
    }
  }

  // ---------- UI ----------
  return (
    <>
      <Dialog.Root
        closeOnInteractOutside={false}
        modal={false}
        placement={"top"}
        scrollBehavior="inside"
        motionPreset="slide-in-right"
        open={showQeuryMenuTable}
        size={"xl"}
        onOpenChange={(e) => setShowQeuryMenuTable(e.open)}
      >
        <Portal>
          <Dialog.Positioner pointerEvents="none">
            <Dialog.Content mr={"70px"} mt={"100px"} width={"fit-content"} pointerEvents={"all"} height={"calc(100% - 150px)"}>
              <Dialog.Header>
                <VStack gap={0}>
                  <Dialog.Title textAlign={"left"} width={"full"}>
                    Query Result
                  </Dialog.Title>
                </VStack>
              </Dialog.Header>
              <Dialog.Body>
                {qeuryMenuTable.length !== 0 && (
                  <>
                    <HStack gap={2} mt={3}>
                      <Button onClick={() => resetViewer(viewerRef.current)}>
                        <LuRotateCcw /> Reset
                      </Button>
                      <Button
                        colorPalette={"red"}
                        onClick={() => {
                          resetViewer(viewerRef.current);
                          setQeuryMenuTable([]);
                          setShowQeuryMenuTable(false);
                        }}
                      >
                        <LuTrash2 /> Remove All
                      </Button>
                    </HStack>
                    <Table.Root size="sm" striped>
                      <Table.Header>
                        <Table.Row>
                          <For each={["Device Name", "Route ID", "Date Time", "Duration", "City", "Actions"]}>{(item, index) => <Table.ColumnHeader key={index}>{item}</Table.ColumnHeader>}</For>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {qeuryMenuTable.map((item, index) => (
                          <Table.Row key={index}>
                            <Table.Cell textAlign="start">{item.device_id}</Table.Cell>
                            <Table.Cell textAlign="start">{item.route_id}</Table.Cell>
                            <Table.Cell textAlign="start">{formatReadableDateTime(item.start_time)}</Table.Cell>
                            <Table.Cell textAlign="start">{parseInt(item.duration)} s</Table.Cell>
                            <Table.Cell textAlign="start">{item.city}</Table.Cell>
                            <Table.Cell textAlign="start">
                              <Button
                                size="sm"
                                p={0}
                                variant="outline"
                                colorScheme="blue"
                                outline="none"
                                border="none"
                                onClick={() => {
                                  if (item?.bbox) showBBoxOnMap(viewerRef.current, item.bbox);
                                  else showToast("No bounding box for this record.", Type.NOTIFICATION.WARNING);
                                }}
                              >
                                <LuFocus />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                colorScheme="blue"
                                p={0}
                                outline="none"
                                border="none"
                                onClick={async () => {
                                  resetViewer(viewerRef.current);
                                  loadGeoJsonAsTruckCzml(viewerRef.current, item.geojson_url, { trailSeconds: 120, multiplier: 1, modelScale: 1.0 }, index);
                                }}
                              >
                                {loadingPercents[index] != null ? loadingPercents[index] + "%" : <LuTvMinimalPlay />}
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </>
                )}
              </Dialog.Body>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Telemetry box (only when we have data) */}
      {telemetry && (
        <Box position={"absolute"} bottom={"100px"} width={"260px"} right={"20px"} p={3} borderRadius={"lg"} bg={"white"} boxShadow="md" zIndex={1000}>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Speed</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.speedMph, 1)} mph`}</Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Latitude</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>
              {fmtCoord(telemetry.lat)}
            </Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Longitude</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>
              {fmtCoord(telemetry.lon)}
            </Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Height</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.h, 2)} m`}</Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Undulation</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.und, 2)} m`}</Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>North Velocity</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.nv, 2)} m/s`}</Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Up Velocity</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.uv, 2)} m/s`}</Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Roll</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.roll, 2)}°`}</Text>
          </HStack>
          <HStack justify={"space-between"} mb={1}>
            <Text fontSize={"14px"}>Pitch</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.pitch, 2)}°`}</Text>
          </HStack>
          <HStack justify={"space-between"}>
            <Text fontSize={"14px"}>Azimuth</Text>
            <Text fontSize={"14px"} fontWeight={"bold"}>{`${fmtNum(telemetry.az, 2)}°`}</Text>
          </HStack>
        </Box>
      )}

      {/* FULL height chart synced with Cesium clock + live cursor */}
      {heightWindow.length > 0 && (
        <Box position={"absolute"} bottom={"35px"} right={"260px"} left={"170px"} p={2} borderRadius={"lg"} bg={"white"} boxShadow="md" zIndex={1000} h="50px">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={heightWindow} margin={{ top: 2, right: 6, bottom: 2, left: 6 }}>
              {/* Treat timestamps as numbers so the domain covers the whole trip */}
              <XAxis type="number" dataKey="t" hide domain={["dataMin", "dataMax"]} />
              <YAxis dataKey="h" hide domain={["auto", "auto"]} />
              <Tooltip formatter={(v) => [`${fmtNum(v, 2)} m`, "Height"]} labelFormatter={(ts) => new Date(ts).toLocaleTimeString()} />
              <Area type="monotone" dataKey="h" dot={false} isAnimationActive={false} strokeWidth={1.5} fillOpacity={0.2} />

              {/* Live time marker (vertical line) and point marker at current height */}
              {chartCursor && (
                <>
                  <ReferenceLine x={chartCursor.t} strokeWidth={1.25} />
                  <ReferenceDot x={chartCursor.t} y={chartCursor.h} r={3} isFront />
                </>
              )}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </>
  );
};

export default QueryTable;
